/**
 * Eva User Detail Drawer (SF-3735).
 *
 * A self-contained, reusable antd Drawer that shows a single user's Eva usage
 * history without leaving the host page. It is opened from any user-list row
 * (e.g. the company detail page, SF-3734) and is also deep-linkable on its own
 * route (see userDetail.tsx). Sections:
 *   • header with name / company / last active
 *   • KPI tiles: this-month spend and % of the per-user $10 monthly cap
 *   • daily chart for the last 30 days
 *   • monthly chart for the last 12 months
 *
 * Data comes from sf-api `GET /admin/eva/usage/user/{userId}/series` (SF-3731)
 * via the shared Eva API client. There is no per-user live totals endpoint in
 * SF-3731, so this-month spend, % of cap, and last-active are derived from the
 * series: the current calendar-month bucket of the monthly series gives the
 * month-to-date spend, and the most recent daily bucket with `messageCount >= 1`
 * gives the last-used day. Every field is null-guarded and money strings are
 * parsed only at display time. The request is gated by `enabled` so the drawer
 * can mount before access control resolves or before a user is selected.
 */
import { useMemo } from 'react';
import {
  Alert,
  Col,
  Drawer,
  Empty,
  Grid,
  Progress,
  Row,
  Space,
  Spin,
  Typography,
  theme,
} from 'antd';
import { DollarOutlined, PercentageOutlined } from '@ant-design/icons';

import { KpiTile } from '../../components/kpi/KpiTile';
import { LiveIndicator } from '../../components/kpi/LiveIndicator';
import { TrendChart, type TrendPoint } from '../../components/charts/TrendChart';
import { formatCurrency } from '../../utils/format';
import {
  currentPeriod,
  dailyTrendRange,
  monthlyTrendRange,
  type Granularity,
} from '../../utils/date';
import { useUserSeries, useLiveUserUsage, type SeriesPoint } from './api/evaUsageClient';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/** Per-user monthly spend cap in USD (override-aware once Wave 5 ships). */
export const USER_MONTHLY_CAP_USD = 10;

/**
 * The release of per-user daily tracking (SF-5146). Company users' daily spend is only
 * saved from this month on, so older spend shows in the monthly chart but not the daily one.
 */
const USER_DAILY_TRACKING_START = 'November 2026';

export interface UserDetailDrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Close handler — fired on esc, overlay click, or the close icon. */
  onClose: () => void;
  /** The user whose Eva history to load. When null the drawer fires no request. */
  userId: string | null | undefined;
  /** Display name for the header (falls back to the user id). */
  userName?: string | null;
  /** Company name for the header subtitle. */
  companyName?: string | null;
  /** Gate the underlying requests on access-control resolution. Defaults to true. */
  enabled?: boolean;
}

/** Map a series of usage points to the cost line plotted by the trend chart. */
function toCostPoints(series: SeriesPoint[] | undefined): TrendPoint[] {
  return (series ?? []).map((point) => ({
    bucket: point.bucket,
    value: Number.isFinite(Number(point.costUsd)) ? Number(point.costUsd) : 0,
  }));
}

/**
 * Format a "YYYY-MM-DD" day key as a full, human-friendly date (UTC). Returns
 * null for malformed input so callers can fall back to an empty state.
 */
function formatLastActive(day: string | null): string | null {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Format a "YYYY-MM" month key as a month/year label (UTC). Used as a coarse
 * fallback for last-active when the user's most recent activity predates the
 * 30-day daily window but still appears in the 12-month monthly series, so the
 * header never shows "—" for a user that genuinely has Eva activity (AC3/AC4).
 */
function formatLastActiveMonth(month: string | null): string | null {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return null;
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Reusable Eva per-user detail drawer. */
export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({
  open,
  onClose,
  userId,
  userName,
  companyName,
  enabled = true,
}) => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();

  // Only fetch while the drawer is open and a user is selected — avoids firing
  // requests for a closed/empty drawer. The client also guards on userId.
  const queryEnabled = enabled && open && Boolean(userId);

  // Derive the chart from/to on every render rather than memoizing with an
  // empty dep list — otherwise a drawer/tab left open across a UTC day or month
  // boundary keeps requesting the original end date and never picks up new
  // buckets until a remount/reload (matches company-detail/overview).
  const dailyRange = dailyTrendRange();
  const monthlyRange = monthlyTrendRange();

  const dailyQuery = useUserSeries(userId, { ...dailyRange, granularity: 'daily' }, {
    enabled: queryEnabled,
  });
  const monthlyQuery = useUserSeries(userId, { ...monthlyRange, granularity: 'monthly' }, {
    enabled: queryEnabled,
  });

  // LIVE current-month per-user usage (SF-3735 Wave 2): chat-service-proxied so
  // the this-month-spend tile + %-of-cap tick up in near real time. Charts stay
  // Mongo (a chart needs per-day buckets; live is a single counter). The route
  // 404s for super-admin ids — with errorNotification off that simply surfaces
  // as `isError`, and we transparently fall back to the series-derived figure
  // (and drop the "Live" badge) rather than crashing or toasting.
  const liveQuery = useLiveUserUsage(userId, { enabled: queryEnabled });

  const dailySeries = dailyQuery.data?.data?.series;
  const monthlySeries = monthlyQuery.data?.data?.series;

  const dailyPoints = useMemo(() => toCostPoints(dailySeries), [dailySeries]);
  const monthlyPoints = useMemo(() => toCostPoints(monthlySeries), [monthlySeries]);

  // This-month spend: prefer the LIVE current-month total (ticks up in real
  // time); fall back to the current calendar-month bucket of the Mongo monthly
  // series when live is unavailable (super-admin 404 / proxy down / error).
  // React Query keeps the last successful `data` on a poll error, so gate the
  // live value on `isError` to keep the figure and the "Live" badge consistent.
  const thisMonth = currentPeriod();
  const live = liveQuery.data?.data;
  const liveMonthSpend = useMemo(() => {
    if (liveQuery.isError || live?.totals?.costUsd == null) return null;
    const n = Number(live.totals.costUsd);
    return Number.isFinite(n) ? n : null;
  }, [liveQuery.isError, live?.totals?.costUsd]);
  const rollupMonthSpend = useMemo(() => {
    const bucket = (monthlySeries ?? []).find((p) => p.bucket === thisMonth);
    if (!bucket) return null;
    const n = Number(bucket.costUsd);
    return Number.isFinite(n) ? n : null;
  }, [monthlySeries, thisMonth]);
  const liveAvailable = liveMonthSpend !== null;
  const monthSpend = liveAvailable ? liveMonthSpend : rollupMonthSpend;

  // % of the per-user monthly cap. Prefer sf-api's server-computed live percent
  // (cap-config aware, null when unconfigured); else derive from the displayed
  // spend against the per-user cap. Null when there is no spend datum yet.
  const capPercent =
    liveAvailable && live?.percentOfMonthlyCap != null
      ? live.percentOfMonthlyCap
      : monthSpend === null
        ? null
        : (monthSpend / USER_MONTHLY_CAP_USD) * 100;

  // Last-used day: most recent daily bucket with at least one message.
  const lastActiveDay = useMemo(() => {
    const active = (dailySeries ?? []).filter((p) => (p.messageCount ?? 0) >= 1);
    if (active.length === 0) return null;
    // Buckets are "YYYY-MM-DD"; lexicographic max equals chronological max.
    return active.reduce((latest, p) => (p.bucket > latest ? p.bucket : latest), active[0].bucket);
  }, [dailySeries]);

  // Fallback last-active month: the most recent monthly bucket with activity.
  // The daily series only covers the trailing 30 days, so a user last active
  // before that window would otherwise show "—" despite having real activity
  // in the 12-month series. We surface the month label in that case (AC3).
  const lastActiveMonth = useMemo(() => {
    const active = (monthlySeries ?? []).filter((p) => (p.messageCount ?? 0) >= 1);
    if (active.length === 0) return null;
    // Buckets are "YYYY-MM"; lexicographic max equals chronological max.
    return active.reduce((latest, p) => (p.bucket > latest ? p.bucket : latest), active[0].bucket);
  }, [monthlySeries]);

  const lastActiveLabel = formatLastActive(lastActiveDay) ?? formatLastActiveMonth(lastActiveMonth);

  const isLoading = dailyQuery.isLoading || monthlyQuery.isLoading;
  const anyError = dailyQuery.isError || monthlyQuery.isError;

  // A bucket counts as activity when it has chat messages OR spend. Spend alone
  // matters: AI features other than chat (dashboard summaries, titles) cost
  // money but send no messages. Messages alone matter too: a chat turn can
  // register before any cost, and should show a zero-line chart, not "No usage".
  const seriesHasActivity = (series: SeriesPoint[] | undefined) =>
    (series ?? []).some((p) => (p.messageCount ?? 0) > 0 || Number(p.costUsd) > 0);
  const dailyHasActivity = seriesHasActivity(dailySeries);
  const monthlyHasActivity = seriesHasActivity(monthlySeries);
  // Live current-month usage also counts as activity: a user who just fired
  // their first Eva query this month registers in the live counter before the
  // nightly rollup populates the series, so the drawer must not show its
  // zero-activity empty state over live usage (SF-3735 Wave 2). Count live
  // MESSAGES, not just spend — a just-started query can register a message
  // before any (or with zero) cost, and a strictly-positive-spend test would
  // wrongly treat that as "no activity" and contradict the live-before-rollup
  // intent. Fall back to spend so a live payload that only carries a cost still
  // counts.
  const liveMonthMessages = useMemo(() => {
    if (liveQuery.isError || live?.totals?.messageCount == null) return null;
    const n = Number(live.totals.messageCount);
    return Number.isFinite(n) ? n : null;
  }, [liveQuery.isError, live?.totals?.messageCount]);
  const liveHasActivity =
    liveAvailable && ((liveMonthMessages ?? 0) > 0 || (monthSpend ?? 0) > 0);
  const hasActivity = dailyHasActivity || monthlyHasActivity || liveHasActivity;
  // Monthly spend with no daily figures means the spend predates per-user daily
  // tracking, so say so instead of showing a daily chart that looks broken.
  const hasSpend = (series: SeriesPoint[] | undefined) =>
    (series ?? []).some((p) => Number(p.costUsd) > 0);
  // Only once both series have loaded without error: a pending or failed daily
  // request proves nothing about missing history.
  const dailyNotTrackedYet =
    !isLoading && !anyError && hasSpend(monthlySeries) && !hasSpend(dailySeries);
  // Don't render the zero-activity empty state until every source that feeds it
  // has settled. The Mongo series gate this directly; the live current-month
  // query gates it too while it is the active source (current month, enabled,
  // not yet errored-out), otherwise a slow live poll could flash "No Eva
  // activity" before the live figure that proves activity arrives.
  const liveStillResolving = liveQuery.isLoading && !liveQuery.isError;
  const showEmptyState =
    !isLoading && !liveStillResolving && !anyError && Boolean(userId) && !hasActivity;

  // KPI-tile skeleton gating (Bugbot): both the live counter and the Mongo
  // monthly series can supply the this-month figures, so a `monthly && live`
  // AND-loading flag dropped the skeleton the moment the faster source settled
  // — flashing an em-dash before the other arrived. Keep the skeleton while the
  // displayed value is still null AND a source that could fill it is in flight.
  const monthSourcesLoading = liveQuery.isLoading || monthlyQuery.isLoading;
  const monthSpendLoading = monthSpend === null && monthSourcesLoading;
  const capPercentLoading = capPercent === null && monthSourcesLoading;

  const header = (
    <div>
      <Title level={4} style={{ marginBottom: 0 }}>
        {userName || userId || 'User'}
      </Title>
      <Space size="small" wrap>
        {companyName ? <Text type="secondary">{companyName}</Text> : null}
        <Text type="secondary">{`Last active: ${lastActiveLabel ?? '\u2014'}`}</Text>
      </Space>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={header}
      width={screens.md ? 720 : '100%'}
      destroyOnHidden
      styles={{ body: { paddingTop: 16 } }}
    >
      {!userId ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No user selected" />
      ) : isLoading ? (
        <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }} />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {anyError && (
            <Alert
              type="warning"
              showIcon
              message="Some usage data could not be loaded"
              description="One or more Eva usage endpoints did not respond. Figures may be incomplete."
            />
          )}

          {showEmptyState ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Eva activity for this user"
            />
          ) : (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <KpiTile
                    title="This Month's Spend"
                    value={formatCurrency(monthSpend)}
                    icon={<DollarOutlined />}
                    loading={monthSpendLoading}
                    badge={liveAvailable ? <LiveIndicator /> : undefined}
                    hint={
                      liveAvailable
                        ? `Live month-to-date Eva spend for ${thisMonth}, updated without waiting for the nightly rollup`
                        : `Month-to-date Eva spend for ${thisMonth}`
                    }
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <KpiTile
                    title="% of Monthly Cap"
                    value={capPercent === null ? '' : `${capPercent.toFixed(1)}%`}
                    icon={<PercentageOutlined />}
                    loading={capPercentLoading}
                    badge={liveAvailable ? <LiveIndicator /> : undefined}
                    hint={`Share of the $${USER_MONTHLY_CAP_USD.toFixed(0)} per-user monthly cap`}
                  />
                </Col>
              </Row>

              {capPercent !== null && (
                <Progress
                  percent={Math.min(capPercent, 100)}
                  status={capPercent >= 100 ? 'exception' : 'active'}
                  strokeColor={capPercent >= 100 ? token.colorError : token.colorPrimary}
                  format={() => `${capPercent.toFixed(1)}%`}
                />
              )}

              <TrendChart
                title="Daily Spend (Last 30 Days)"
                data={dailyPoints}
                granularity={'daily' as Granularity}
                loading={dailyQuery.isLoading}
                valueFormatter={(v) => formatCurrency(v)}
                isEmpty={!dailyHasActivity}
              />
              {dailyNotTrackedYet && (
                <Text type="secondary">
                  {`Daily figures are only kept from ${USER_DAILY_TRACKING_START}.`}
                </Text>
              )}

              <TrendChart
                title="Monthly Spend (Last 12 Months)"
                data={monthlyPoints}
                granularity={'monthly' as Granularity}
                loading={monthlyQuery.isLoading}
                valueFormatter={(v) => formatCurrency(v)}
                isEmpty={!monthlyHasActivity}
              />
            </>
          )}
        </Space>
      )}
    </Drawer>
  );
};
